// src/utils/tagImageMatcher.js
// Pure winner-selection for the tag illustration layer (docs/briefs/2026-08-11-image-layer-build.md
// §4). No fetching here — lives in /src/utils/ (matching eventsImportParsing.js's precedent for a
// pure events-domain helper), not /src/data/, since it does no supabaseFetch call of its own.
// Callers already hold the vocabulary (getTagVocabulary()) and an event's tag_slugs.

/**
 * Pick the image URL for an event given its tags, per the vocabulary's own order
 * (report D8 / brief §4): winner = the tag ranking highest by
 * (tag_categories.display_order, tag_terms.display_order) among the event's own
 * tags — skipping any tag whose term has no image_url yet (a term can exist
 * before its illustration file is deployed; this must not crash or pick a 404).
 *
 * @param {string[]|null} tagSlugs - an event's articles.tag_slugs
 * @param {Array<{id: string, display_order: number, terms: Array<{id: string, display_order: number, image_url: string|null}>}>} tagVocabulary
 *   - getTagVocabulary()'s return shape (categories, each with nested terms)
 * @returns {string|null} the winning term's image_url, or null if no tag matches
 */
export function pickTagImage(tagSlugs, tagVocabulary) {
  if (!tagSlugs || tagSlugs.length === 0 || !tagVocabulary || tagVocabulary.length === 0) {
    return null;
  }

  const slugSet = new Set(tagSlugs);
  let winner = null; // { catOrder, termOrder, image_url }

  for (const category of tagVocabulary) {
    for (const term of category.terms) {
      if (!slugSet.has(term.id) || !term.image_url) continue;
      const candidate = { catOrder: category.display_order, termOrder: term.display_order, image_url: term.image_url };
      if (
        !winner ||
        candidate.catOrder < winner.catOrder ||
        (candidate.catOrder === winner.catOrder && candidate.termOrder < winner.termOrder)
      ) {
        winner = candidate;
      }
    }
  }

  return winner ? winner.image_url : null;
}

/** Is this cover_image_url one of the tag illustrations (as opposed to a steward's own photo)? */
export function isTagImageUrl(url) {
  return typeof url === 'string' && url.startsWith('/images/tags/');
}
